import React from 'react'
import { X } from 'lucide-react'
import { Input, Label, Modal } from '@/shared/ui'
import { useCommunityDraftAttachments } from '@/modules/comunidade/hooks/useCommunityDraftAttachments'
import { extractCommunityMessagePlainText } from '@/modules/comunidade/utils/communityMessageContent'
import type { ChatPostAttachment } from '../services/chatPostAttachmentsService'
import { PostAttachmentsEditor, PostComposer } from '@/modules/post-shared'

export type NewChatModalSubmitParams = {
  title: string
  html: string
  newAttachments: File[]
  removeAttachmentIds: number[]
}

type NewChatModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (params: NewChatModalSubmitParams) => void | Promise<void>
  chatId?: number
  initialTitle?: string
  initialHtml?: string
  initialAttachments?: ChatPostAttachment[]
  mode?: 'create' | 'edit'
}

export function NewChatModal({
  open,
  onOpenChange,
  onSubmit,
  chatId,
  initialTitle,
  initialHtml,
  initialAttachments,
  mode = 'create',
}: NewChatModalProps) {
  const [title, setTitle] = React.useState(initialTitle || '')
  const [html, setHtml] = React.useState(initialHtml || '')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [existingAttachments, setExistingAttachments] = React.useState<ChatPostAttachment[]>(
    Array.isArray(initialAttachments) ? initialAttachments : [],
  )
  const [removeAttachmentIds, setRemoveAttachmentIds] = React.useState<number[]>([])

  const {
    attachments: draftAttachments,
    addFiles: addDraftFiles,
    removeAttachment: removeDraftAttachment,
    clear: clearDraftAttachments,
    error: attachmentError,
  } = useCommunityDraftAttachments()

  const isEdit = mode === 'edit'

  const hasContent = React.useMemo(() => {
    const text = extractCommunityMessagePlainText(html || '')
    return !!String(text || '').trim()
  }, [html])

  const canSubmit = React.useMemo(() => {
    const hasTitle = !!title.trim()
    return hasTitle && hasContent
  }, [hasContent, title])

  const reset = React.useCallback(() => {
    setTitle(initialTitle || '')
    setHtml(initialHtml || '')
    setError(null)
    setExistingAttachments(Array.isArray(initialAttachments) ? initialAttachments : [])
    setRemoveAttachmentIds([])
    clearDraftAttachments()
  }, [clearDraftAttachments, initialAttachments, initialHtml, initialTitle])

  React.useEffect(() => {
    if (!open) return
    reset()
  }, [open, reset])

  const handleClose = React.useCallback(() => {
    onOpenChange(false)
    reset()
  }, [onOpenChange, reset])

  const handleRemoveExisting = React.useCallback((id: number) => {
    setExistingAttachments((prev) => prev.filter((att) => att.id !== id))
    setRemoveAttachmentIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])

  const handleSubmit = React.useCallback(async () => {
    const trimmedTitle = title.trim()
    const trimmedHtml = String(html || '').trim()
    const newFiles = draftAttachments.map((a) => a.file).filter(Boolean)

    if (!trimmedTitle) {
      setError('Preencha o titulo antes de enviar.')
      return
    }

    if (!hasContent) {
      setError('Preencha a descricao antes de enviar.')
      return
    }

    setSaving(true)
    setError(null)
    try {
      if (onSubmit) {
        await onSubmit({
          title: trimmedTitle,
          html: trimmedHtml,
          newAttachments: newFiles,
          removeAttachmentIds,
        })
      }
      onOpenChange(false)
      reset()
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }, [
    draftAttachments,
    hasContent,
    html,
    onOpenChange,
    onSubmit,
    removeAttachmentIds,
    reset,
    title,
  ])

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          handleClose()
        } else {
          onOpenChange(true)
        }
      }}
      className="w-full max-w-2xl p-6"
    >
      <div className="space-y-4">
        <div className="relative pr-10">
          <button
            type="button"
            aria-label="Fechar"
            disabled={saving}
            onClick={handleClose}
            className="absolute right-0 top-0 rounded p-1 text-foreground/70 hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold">
            {isEdit ? 'Editar post' : 'Novo post'}
          </h2>
          <p className="text-xs text-foreground/60">
            {isEdit
              ? 'Altere o titulo, o conteudo e os anexos do post.'
              : 'Crie um novo post para o chat da comunidade.'}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="chat-title">Titulo</Label>
          <Input
            id="chat-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex.: Duvida sobre tendencia do BTC"
          />
        </div>

        <div className="space-y-2">
          <Label>Descricao</Label>

          {attachmentError && <div className="text-[11px] text-red-400">{attachmentError}</div>}

          <PostAttachmentsEditor
            existingAttachments={existingAttachments}
            onRemoveExisting={handleRemoveExisting}
            draftAttachments={draftAttachments}
            onRemoveDraft={removeDraftAttachment}
          />

          <PostComposer
            value={html}
            onChange={setHtml}
            placeholder="Digite sua mensagem aqui..."
            onAddFiles={addDraftFiles}
            disabled={saving}
            onSubmit={handleSubmit}
            submitDisabled={saving || !canSubmit}
          />

          <p className="text-xs text-foreground/60">
            Use o editor para formatar o texto, anexar arquivos/imagens, inserir emojis e mencionar usuarios com @.
          </p>
        </div>

        {error && <div className="text-xs text-destructive">{error}</div>}
      </div>
    </Modal>
  )
}
